/**
 * W1 — Action Resolver ★ keystone
 * (object context, role, status, plane) → ordered WorkbenchAction[]
 */

import type { UniversalBaseEntity } from '@shared/types/entity';
import { getRegisteredWorkbenchActions } from './action-registry';
import { CORE_ACTION_RULE_TABLE } from './action-rule-table';
import {
  collectDomainActionRuleEntries,
  resolveDomainActionFamily,
  DOMAIN_SUPERSEDED_CORE_ACTION_IDS,
} from './domain-action-rule-table';
import { SELLABLE_GOLDEN_ACTION_RULE_TABLE } from './sellable-action-rule-table';
import type {
  ActionRuleCondition,
  ActionRuleEntry,
  ResolveActionsInput,
  WorkbenchAction,
  WorkbenchActionPlacement,
  WorkbenchActionSurface,
} from './action-types';

/** W2 — table-row: primary + 1 secondary (max 2 visible) + More */
const DEFAULT_VISIBLE_ROW_ACTIONS = 2;

function matchesCondition(
  condition: ActionRuleCondition,
  input: ResolveActionsInput & { businessDomain: string }
): boolean {
  if (condition.domains !== '*' && condition.domains !== undefined) {
    if (!condition.domains.includes(input.businessDomain)) {
      return false;
    }
  }
  if (condition.roles !== '*' && condition.roles !== undefined) {
    if (!condition.roles.includes(input.role)) {
      return false;
    }
  }
  if (condition.statuses !== '*' && condition.statuses !== undefined) {
    if (!condition.statuses.includes(input.status)) {
      return false;
    }
  }
  if (condition.planes !== '*' && condition.planes !== undefined) {
    if (!condition.planes.includes(input.plane)) {
      return false;
    }
  }
  return true;
}

function collectRuleActions(
  table: readonly ActionRuleEntry[],
  input: ResolveActionsInput & { businessDomain: string }
): WorkbenchAction[] {
  const collected: WorkbenchAction[] = [];
  for (const entry of table) {
    if (!matchesCondition(entry.condition, input)) {
      continue;
    }
    for (const action of entry.actions) {
      collected.push({ ...action, placement: 'more' });
    }
  }
  return collected;
}

function dedupeById(actions: WorkbenchAction[]): WorkbenchAction[] {
  const seen = new Set<string>();
  const result: WorkbenchAction[] = [];
  for (const action of actions) {
    if (seen.has(action.id)) {
      continue;
    }
    seen.add(action.id);
    result.push(action);
  }
  return result;
}

function hasPermission(action: WorkbenchAction, permissionKeys: string[] | undefined): boolean {
  if (!action.permissionKey) {
    return true;
  }
  if (!permissionKeys || permissionKeys.length === 0) {
    return true;
  }
  return permissionKeys.some(
    (key) => key === action.permissionKey || key.endsWith(`:${action.permissionKey}`)
  );
}

function filterByPermission(
  actions: WorkbenchAction[],
  permissionKeys: string[] | undefined
): WorkbenchAction[] {
  return actions
    .map((action) => {
      if (!hasPermission(action, permissionKeys)) {
        return { ...action, forbidden: true, label: '' };
      }
      return action;
    })
    .filter((action) => !action.forbidden || action.placement === 'command-only');
}

function filterBySurface(
  actions: WorkbenchAction[],
  surface: WorkbenchActionSurface | undefined
): WorkbenchAction[] {
  if (!surface) {
    return actions;
  }
  return actions.filter((action) => action.surfaces.includes(surface));
}

function assignPlacement(
  actions: WorkbenchAction[],
  surface: WorkbenchActionSurface | undefined
): WorkbenchAction[] {
  const sorted = [...actions].sort((a, b) => b.priority - a.priority);
  const maxVisible =
    surface === 'table-row' || surface === 'detail-header'
      ? DEFAULT_VISIBLE_ROW_ACTIONS
      : sorted.length;

  return sorted.map((action, index) => {
    if (action.commandOnly) {
      return { ...action, placement: 'command-only' as WorkbenchActionPlacement };
    }
    if (index === 0) {
      return { ...action, placement: 'primary' as WorkbenchActionPlacement };
    }
    if (index < maxVisible) {
      return { ...action, placement: 'secondary' as WorkbenchActionPlacement };
    }
    return { ...action, placement: 'more' as WorkbenchActionPlacement };
  });
}

export function resolveActions(
  object: UniversalBaseEntity,
  input: ResolveActionsInput
): WorkbenchAction[] {
  const businessDomain = input.businessDomain ?? object.businessDomain ?? 'entity';
  const context = { ...input, businessDomain, status: input.status || String(object.status) };

  const fromSellableRules =
    businessDomain === 'sellable_item'
      ? collectRuleActions(SELLABLE_GOLDEN_ACTION_RULE_TABLE, context)
      : [];
  const fromDomainRules = collectRuleActions(
    collectDomainActionRuleEntries(businessDomain),
    context
  );
  const hasDomainManifest = resolveDomainActionFamily(businessDomain) !== null;
  const fromCoreRules =
    businessDomain === 'sellable_item'
      ? []
      : collectRuleActions(CORE_ACTION_RULE_TABLE, context).filter(
          (action) =>
            !hasDomainManifest ||
            !(DOMAIN_SUPERSEDED_CORE_ACTION_IDS as readonly string[]).includes(action.id)
        );
  const fromRules = [...fromSellableRules, ...fromDomainRules, ...fromCoreRules];
  const fromRegistry = getRegisteredWorkbenchActions().map((action) => ({
    ...action,
    placement: action.placement ?? ('more' as WorkbenchActionPlacement),
  }));

  const merged = dedupeById([...fromRules, ...fromRegistry]);
  const permitted = input.skipLegacyPermissionFilter
    ? merged
    : filterByPermission(merged, input.permissionKeys);
  const surfaced = filterBySurface(permitted, input.surface);
  return assignPlacement(surfaced, input.surface);
}

export function getVisibleActions<T extends WorkbenchAction>(actions: readonly T[]): T[] {
  return actions.filter(
    (action) => action.placement === 'primary' || action.placement === 'secondary'
  );
}

export function getMoreActions<T extends WorkbenchAction>(actions: readonly T[]): T[] {
  return actions.filter((action) => action.placement === 'more');
}

export function getCommandActions(actions: WorkbenchAction[]): WorkbenchAction[] {
  return actions.filter(
    (action) =>
      action.placement === 'command-only' ||
      action.surfaces.includes('command') ||
      action.placement === 'more'
  );
}
